import EvidenceSections from 'components/forms/evidence-field/EvidenceSections';
import useEvidenceSections from 'components/forms/evidence-field/use-evidence-sections';
import { DepartmentLabel, useCurrentDepartmentText, useDepartmentText } from 'model/DepartmentTerminology';
import {
  Alert,
  Badge,
  Box,
  Divider,
  Flex,
  Group,
  Input,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  TextInput,
  Textarea,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { randomId } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCalendar, IconCheck, IconCopy, IconFile, IconPlus, IconTrashX } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import { useFileManager } from 'api/file-manager/file-manager-context';
import Button from 'components/Button';
import FormInputGroup from 'components/forms/FormInputGroup';
import FormWrapper from 'components/forms/FormWrapper';
import HrPersonFileForm, {
  HrPersonFileCreateFormValues,
  HrPersonFileEditFormValues,
} from 'components/forms/hr/HrPersonFileForm';
import HrExecuteTermModal from 'components/modals/hr-execute-term/HrExecuteTermModal';
import HrTermStateModal, { HrRelatedTermFormValues } from 'components/modals/hr-term-state/HrTermStateModal';
import HrPersonFiles, { HrPersonFormFile } from 'components/forms/hr/HrPersonFiles';
import HrPersonTermForm from 'components/forms/hr/HrPersonTermForm';
import HrTermVariantCheckboxes from 'components/forms/hr/HrTermVariantCheckboxes';
import EvidenceValueInput from 'components/forms/evidence-field/EvidenceValueInput';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { DateInput } from 'components/inputs/DateInput';
import { DepartmentSelect } from 'components/selects/DepartmentSelect';
import { HrPersonTermActions } from 'components/tables/data/hr/HrActions';
import H3Medium from 'components/typography/H3Medium';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { HR_EDIT_PERSON_PAGE_PATH, HR_PEOPLE_PAGE_PATH, HR_TERM_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import { formatHrStatus } from 'pages/hr-module/hr-formatters';

export interface HrPersonFormProps {
  create?: boolean;
  copy?: boolean;
  edit?: boolean;
  termMode?: 'create';
  fileMode?: 'create' | 'edit';
}

interface PersonFormValues {
  firstName: string;
  lastName: string;
  departmentId: string;
  userId: string;
  evidence: Record<string, string | number | boolean>;
  terms: Array<{
    key: string;
    termId?: number;
    version?: number;
    termTypeId: string;
    termTypeName?: string;
    lifecycleStatus?: string;
    lastExecutionDate: string;
    nextDueDate: string;
    recurrenceQuantity: number;
    recurrenceUnit: string;
    note: string;
    checkedVariantIds: number[];
    changed: boolean;
    deleted: boolean;
  }>;
  files: HrPersonFormFile[];
}

/** Maps authoritative file rows into the combined person form. */
function personFilesForForm(files: any[]): PersonFormValues['files'] {
  return files.map((file) => ({
    key: `file-${file.personFileId}`,
    personFileId: file.personFileId,
    version: file.version,
    fileId: file.fileId,
    fileName: file.originalName ?? file.displayName ?? 'súbor',
    displayName: file.displayName ?? file.originalName ?? '',
    categoryId: String(file.categoryId),
    categoryName: file.categoryName,
    uploadedAt: file.createdAt ?? file.updatedAt,
    mimeType: file.mimeType,
    isPrimaryPhoto: Boolean(file.isPrimaryPhoto),
    wasPrimaryPhoto: Boolean(file.isPrimaryPhoto),
    changed: false,
    deleted: false,
  }));
}

/** Extracts a user-facing status from an API error without coupling to Axios. */
function errorStatus(error: any) {
  return error?.response?.status;
}

/** Returns only the value property selected by the field's current definition type. */
function evidenceFieldValue(field: any): string | number | boolean {
  switch (field.fieldType) {
    case 'number':
      return field.fieldNumberValue ?? '';
    case 'date':
      return field.fieldDateValue ?? '';
    case 'select':
      return field.fieldSelectValue ?? '';
    case 'predefined':
      return field.fieldPredefinedValue ?? '';
    case 'checkbox':
      return field.fieldBooleanValue ?? false;
    default:
      return field.fieldValue ?? '';
  }
}

/** Person create/copy/edit surface that becomes fully read-only without record-level edit permission. */
export default function HrPersonForm({
  create = false,
  copy = false,
  edit = false,
  termMode,
  fileMode,
}: HrPersonFormProps) {
  const departmentText = useDepartmentText();
  const currentDepartmentText = useCurrentDepartmentText();
  const { personId, personFileId } = useParams();
  const standaloneRouteKey = termMode
    ? `person:${personId ?? ''}:term:${termMode}`
    : fileMode
    ? `person:${personId ?? ''}:file:${fileMode}:${personFileId ?? ''}`
    : '';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAction } = useApi();
  const { getFileMetadata } = useFileManager();
  const { isMobile, isTablet } = useResponsiveDesign();
  const theme = useMantineTheme();
  const { confirm } = useConfirm();
  const creating = create || copy;
  const [person, setPerson] = useState<any>(null);
  const [evidenceDefinitions, setEvidenceDefinitions] = useState<any[]>([]);
  const [linkedUsers, setLinkedUsers] = useState<any[]>([]);
  const [formOrganizationId, setFormOrganizationId] = useState<number | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [terms, setTerms] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [relatedLoaded, setRelatedLoaded] = useState(false);
  const [initializedStandaloneRoute, setInitializedStandaloneRoute] = useState('');
  const [termTypes, setTermTypes] = useState<any[]>([]);
  const [fileCategories, setFileCategories] = useState<any[]>([]);
  const [certificateOptions, setCertificateOptions] = useState<any[]>([]);
  const [certificateOptionsLoading, setCertificateOptionsLoading] = useState(false);
  const [handledDeepAction, setHandledDeepAction] = useState('');
  const [termModal, setTermModal] = useState<'create' | 'execute' | 'pair' | 'reactivate' | null>(null);
  const [selectedTerm, setSelectedTerm] = useState<any>(null);
  const [editingFile, setEditingFile] = useState<any>(null);
  const [relatedFormPersonId, setRelatedFormPersonId] = useState<number | null>(null);
  const [formCanAccessFiles, setFormCanAccessFiles] = useState(false);
  const highlightedTermId = useMemo(() => {
    const termIdParam = searchParams.get('highlightTermId');
    if (!termIdParam) {
      return undefined;
    }
    const termId = Number(termIdParam);
    return Number.isNaN(termId) ? undefined : termId;
  }, [searchParams]);
  const [isTermHighlightVisible, setIsTermHighlightVisible] = useState(false);
  const departmentRequestSequence = useRef(0);
  const highlightedTermRef = useRef<HTMLDivElement>(null);

  const form = useForm<PersonFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      departmentId: '',
      userId: '',
      evidence: {},
      terms: [],
      files: [],
    },
    validate: {
      firstName: (value) => (value.trim() ? null : 'Pole je povinné'),
      lastName: (value) => (value.trim() ? null : 'Pole je povinné'),
      departmentId: (value) =>
        value ? null : <DepartmentLabel objectLabel="Vyberte položku úrovne" departmentLabel="Vyberte stredisko" />,
    },
  });
  const termForm = useForm<HrRelatedTermFormValues>({
    initialValues: {
      termTypeId: '',
      nextDueDate: '',
      recurrenceQuantity: 1,
      recurrenceUnit: 'years',
      note: '',
      checkedVariantIds: [],
      certificateId: '',
      certificateNumber: '',
      renewalDate: '',
      certificateFileId: '',
      stampFileId: '',
    },
  });

  const fileForm = useForm<HrPersonFileEditFormValues>({
    initialValues: { displayName: '', categoryId: '' },
    validate: {
      displayName: (value) => (value.trim() ? null : 'Pole je povinné'),
      categoryId: (value) => (value ? null : 'Vyberte kategóriu'),
    },
  });

  const fileCreateForm = useForm<HrPersonFileCreateFormValues>({
    initialValues: { fileId: '', fileName: '', mimeType: '', categoryId: null as string | null },
  });

  const activeRouteForm = termMode
    ? termForm
    : fileMode === 'create'
    ? fileCreateForm
    : fileMode === 'edit'
    ? fileForm
    : form;
  useDirtyFormHandler(activeRouteForm, true);

  const loadPerson = useCallback(async () => {
    setLoading(true);
    setError(null);
    setConflict(false);
    try {
      if (create) {
        const emptyValues: PersonFormValues = {
          firstName: '',
          lastName: '',
          departmentId: '',
          userId: '',
          evidence: {},
          terms: [],
          files: [],
        };
        form.setValues(emptyValues);
        form.resetDirty(emptyValues);
        setPerson(null);
        setEvidenceDefinitions([]);
        setLinkedUsers([]);
        setFormOrganizationId(null);
        setFormCanAccessFiles(false);
        return;
      }

      const getPerson = getAction('HrGetPerson');
      const response = await getPerson({ parameters: { personId: personId! } });
      if (copy && !response.actionPermissions.edit) {
        setPerson(null);
        setError('Nemáte oprávnenie kopírovať túto osobu.');
        return;
      }
      const options = response.actionPermissions.edit
        ? await getAction('HrGetPersonFormOptions')({
            query: {
              filters: {
                departmentId: response.departmentId,
                ...(edit ? { personId: response.personId } : {}),
              },
            },
          })
        : {
            evidenceFields: response.evidenceFields,
            linkedUsers: [],
            organizationId: response.organizationId,
          };
      setPerson(response);
      setFormCanAccessFiles(Boolean(response.actionPermissions.accessFiles));
      setEvidenceDefinitions(options.evidenceFields);
      setLinkedUsers(options.linkedUsers);
      setFormOrganizationId(options.organizationId);
      const personValues: PersonFormValues = {
        firstName: response.firstName,
        lastName: copy ? `${response.lastName} (Kópia)` : response.lastName,
        departmentId: String(response.departmentId),
        userId: copy ? '' : response.userId ? String(response.userId) : '',
        evidence: Object.fromEntries(
          response.evidenceFields.map((field) => [String(field.evidenceFieldId), evidenceFieldValue(field)])
        ),
        terms: [],
        files: [],
      };
      form.setValues(personValues);
      form.resetDirty(personValues);
    } catch (caught) {
      const status = errorStatus(caught);
      setError(
        status === 403
          ? 'Nemáte oprávnenie zobraziť túto osobu.'
          : status === 404
          ? 'Osoba neexistuje.'
          : 'Osobu sa nepodarilo načítať.'
      );
    } finally {
      setLoading(false);
    }
  }, [copy, create, edit, getAction, personId]);

  const loadRelated = useCallback(async () => {
    if (!personId || !person) {
      return;
    }

    setRelatedLoaded(false);
    setRelatedFormPersonId(null);
    try {
      const [nextTerms, nextTermTypes, nextFiles, nextCategories] = await Promise.all([
        getAction('HrListTerms')({ query: { filters: { personId: Number(personId) } } }),
        copy || person.actionPermissions.manageTerms
          ? getAction('HrListTermTypes')({
              query: { filters: { organizationId: person.organizationId, status: 'active' } },
            })
          : Promise.resolve([]),
        person.actionPermissions.accessFiles && !copy
          ? getAction('HrListPersonFiles')({ parameters: { personId } })
          : Promise.resolve([]),
        person.actionPermissions.accessFiles && (copy || person.actionPermissions.edit)
          ? getAction('HrListFileCategories')({
              query: { filters: { organizationId: person.organizationId, status: 'active' } },
            })
          : Promise.resolve([]),
      ]);
      setTerms(
        nextTerms.map((term) => ({
          ...term,
          rawStatus: term.status,
          status: term.status === 'active' ? '1' : '0',
        }))
      );
      setTermTypes(nextTermTypes);
      const filesWithMetadata = await Promise.all(
        nextFiles.map(async (file) => {
          if (!file.fileId) {
            return { ...file, mimeType: undefined };
          }
          try {
            const metadata = await getFileMetadata({ fileId: file.fileId });
            return { ...file, mimeType: metadata.mimeType };
          } catch (caught) {
            panic(caught as Error);
            return { ...file, mimeType: undefined };
          }
        })
      );
      setFiles(filesWithMetadata);
      setFileCategories(nextCategories);
    } catch (caught) {
      panic(caught as Error);
      if (copy) {
        setError('Súvisiace údaje osoby sa nepodarilo načítať. Kopírovanie nie je možné.');
      }
    } finally {
      setRelatedLoaded(true);
    }
  }, [copy, getAction, getFileMetadata, person, personId]);

  useEffect(() => {
    loadPerson();
  }, [loadPerson]);

  useEffect(() => {
    loadRelated();
  }, [loadRelated]);

  useEffect(() => {
    if ((!edit && !copy) || !person || !relatedLoaded || relatedFormPersonId === person.personId) {
      return;
    }
    const nextTerms: PersonFormValues['terms'] = terms.map((term) => ({
      key: copy ? randomId() : `term-${term.termId}`,
      ...(copy ? {} : { termId: term.termId, version: term.version }),
      termTypeId: String(term.termTypeId),
      termTypeName: term.termTypeName,
      lifecycleStatus: copy ? 'active' : term.rawStatus ?? term.status,
      lastExecutionDate: copy ? '' : term.lastExecutionDate ?? '',
      nextDueDate: term.nextDueDate ?? '',
      recurrenceQuantity: term.recurrence?.quantity ?? (term.kind === 'technician-certificate' ? 1 : 0),
      recurrenceUnit: term.recurrence?.unit ?? (term.kind === 'technician-certificate' ? 'years' : 'none'),
      note: term.note ?? '',
      checkedVariantIds: (term.checkedVariants ?? []).map((variant: any) => variant.variantId),
      changed: false,
      deleted: false,
    }));
    const nextFiles = copy ? [] : personFilesForForm(files);
    const authoritativeValues: PersonFormValues = {
      firstName: person.firstName,
      lastName: copy ? `${person.lastName} (Kópia)` : person.lastName,
      departmentId: String(person.departmentId),
      userId: copy ? '' : person.userId ? String(person.userId) : '',
      evidence: Object.fromEntries(
        person.evidenceFields.map((field: any) => [String(field.evidenceFieldId), evidenceFieldValue(field)])
      ),
      terms: nextTerms,
      files: nextFiles,
    };
    form.setValues({ terms: nextTerms, files: nextFiles });
    form.resetDirty(authoritativeValues);
    setRelatedFormPersonId(person.personId);
  }, [copy, edit, files, person, relatedFormPersonId, relatedLoaded, terms]);

  /** Loads authoritative evidence and link choices whenever the owning department changes. */
  const changeDepartment = async (departmentId: string | null) => {
    const requestSequence = ++departmentRequestSequence.current;
    const nextDepartmentId = departmentId ?? '';
    const previousOrganizationId = formOrganizationId;
    form.setFieldValue('departmentId', nextDepartmentId);
    if (!nextDepartmentId) {
      setOptionsLoading(false);
      form.setFieldValue('userId', '');
      setEvidenceDefinitions([]);
      setLinkedUsers([]);
      setTermTypes([]);
      setFileCategories([]);
      setFormOrganizationId(null);
      setFormCanAccessFiles(false);
      return;
    }

    setOptionsLoading(true);
    try {
      const options = await getAction('HrGetPersonFormOptions')({
        query: {
          filters: {
            departmentId: Number(nextDepartmentId),
            ...(edit && person ? { personId: person.personId } : {}),
          },
        },
      });
      const [nextTermTypes, filePermission] = await Promise.all([
        getAction('HrListTermTypes')({
          query: { filters: { organizationId: options.organizationId, status: 'active' } },
        }),
        getAction('AuthUserHasPermissionInDepartment')({
          parameters: {
            departmentId: nextDepartmentId,
            permissionSlug: 'access-hr-files',
          },
        }),
      ]);
      const canAccessFiles = Boolean(filePermission.hasPermission);
      const nextFileCategories = canAccessFiles
        ? await getAction('HrListFileCategories')({
            query: { filters: { organizationId: options.organizationId, status: 'active' } },
          })
        : [];
      if (requestSequence !== departmentRequestSequence.current) {
        return;
      }
      setEvidenceDefinitions(options.evidenceFields);
      setLinkedUsers(options.linkedUsers);
      setFormOrganizationId(options.organizationId);
      setTermTypes(nextTermTypes);
      setFileCategories(nextFileCategories);
      setFormCanAccessFiles(canAccessFiles);
      if (!canAccessFiles && form.values.files.some((file) => file.changed || file.deleted || !file.personFileId)) {
        notifications.show({
          title: copy
            ? currentDepartmentText(
                'Súbory sa do cieľovej položky úrovne nedajú skopírovať',
                'Súbory sa do cieľového strediska nedajú skopírovať'
              )
            : currentDepartmentText(
                'Súbory sa v cieľovej položke úrovne nedajú upravovať',
                'Súbory sa v cieľovom stredisku nedajú upravovať'
              ),
          message: copy
            ? 'Predvyplnené súborové asociácie boli z kópie odstránené. Zdrojová osoba zostáva nezmenená.'
            : 'Rozpracované zmeny súborov boli z formulára odstránené. Existujúce súbory zostávajú zachované.',
          color: 'orange',
        });
        form.setFieldValue('files', copy ? [] : personFilesForForm(files));
      }
      if (previousOrganizationId !== null && previousOrganizationId !== options.organizationId) {
        form.setFieldValue('userId', '');
        form.setFieldValue('terms', []);
        form.setFieldValue('files', []);
      }
    } catch (caught) {
      if (requestSequence !== departmentRequestSequence.current) {
        return;
      }
      panic(caught as Error);
      form.setFieldValue('departmentId', create || !person ? '' : String(person.departmentId));
      if (create) {
        form.setFieldValue('userId', '');
        setEvidenceDefinitions([]);
        setLinkedUsers([]);
        setTermTypes([]);
        setFileCategories([]);
        setFormOrganizationId(null);
        setFormCanAccessFiles(false);
      } else if (person) {
        setFormCanAccessFiles(Boolean(person.actionPermissions.accessFiles));
      }
    } finally {
      if (requestSequence === departmentRequestSequence.current) {
        setOptionsLoading(false);
      }
    }
  };

  const personEvidence = useMemo(() => evidenceDefinitions, [evidenceDefinitions]);
  /** Persists the staged device-style term and file rows through their authoritative HR endpoints. */
  const persistPersonRelated = async (targetPersonId: string, values: PersonFormValues, allowFiles: boolean) => {
    const termOperations: Array<() => Promise<unknown>> = [];

    values.terms.forEach((term) => {
      if (term.lifecycleStatus && term.lifecycleStatus !== 'active') {
        return;
      }
      if (term.deleted && term.termId) {
        termOperations.push(() =>
          getAction('HrSetTermStatus')({
            parameters: { termId: String(term.termId) },
            payload: { version: term.version!, status: 'archived' },
          })
        );
      } else if (!term.deleted && (!term.termId || term.changed)) {
        const payload = {
          termTypeId: Number(term.termTypeId),
          lastExecutionDate: term.lastExecutionDate || null,
          nextDueDate: term.nextDueDate,
          recurrence:
            term.recurrenceUnit !== 'none'
              ? { quantity: term.recurrenceQuantity, unit: term.recurrenceUnit }
              : undefined,
          note: term.note.trim() || undefined,
          checkedVariantIds: term.checkedVariantIds,
        } as any;
        termOperations.push(() =>
          term.termId
            ? getAction('HrUpdateTerm')({
                parameters: { termId: String(term.termId) },
                payload: { ...payload, version: term.version! },
              })
            : getAction('HrCreateTerm')({ parameters: { personId: targetPersonId }, payload })
        );
      }
    });

    const submittedFiles = allowFiles ? values.files.map((file) => ({ ...file })) : [];
    const metadataUpdates = submittedFiles.filter((file) => file.personFileId && file.changed && !file.deleted);
    const newFiles = submittedFiles.filter((file) => !file.deleted && !file.personFileId);
    const deletedFiles = submittedFiles.filter((file) => file.deleted && file.personFileId);
    const desiredPrimaryKey = submittedFiles.find((file) => !file.deleted && file.isPrimaryPhoto)?.key;
    const initialPrimary = submittedFiles.find((file) => file.wasPrimaryPhoto);
    const primaryChangesSeparately =
      initialPrimary?.deleted !== true && initialPrimary?.key !== desiredPrimaryKey ? 1 : 0;
    const total =
      termOperations.length +
      metadataUpdates.length +
      (newFiles.length > 0 ? 1 : 0) +
      primaryChangesSeparately +
      deletedFiles.length;

    let completed = 0;
    /** Runs one ordered related-data operation and annotates partial failures. */
    const run = async <T,>(operation: () => Promise<T>): Promise<T> => {
      try {
        const result = await operation();
        completed += 1;
        return result;
      } catch (cause) {
        throw Object.assign(new Error('Person related-data save failed.'), {
          relatedSave: true,
          completed,
          total,
          cause,
        });
      }
    };

    for (const operation of termOperations) {
      await run(operation);
    }

    /** Merges an authoritative person-file response into its staged row. */
    const mergeFile = (key: string, authoritative: any) => {
      const index = submittedFiles.findIndex((file) => file.key === key);
      submittedFiles[index] = {
        ...submittedFiles[index],
        ...authoritative,
        isPrimaryPhoto: Boolean(authoritative.isPrimaryPhoto),
      };
    };

    for (const file of metadataUpdates) {
      const updated = await run(() =>
        getAction('HrUpdatePersonFile')({
          parameters: { personId: targetPersonId, personFileId: String(file.personFileId) },
          payload: {
            version: file.version!,
            displayName: file.displayName.trim() || file.fileName,
            categoryId: Number(file.categoryId),
          },
        })
      );
      mergeFile(file.key, updated);
    }

    if (newFiles.length > 0) {
      const added = await run(() =>
        getAction('HrAddPersonFiles')({
          parameters: { personId: targetPersonId },
          payload: {
            files: newFiles.map((file) => ({
              fileId: file.fileId,
              fileName: file.fileName,
              displayName: file.displayName.trim() || file.fileName,
              categoryId: Number(file.categoryId),
              isPrimaryPhoto: false,
            })),
          },
        })
      );
      added.files.forEach((authoritative) => {
        const file = submittedFiles.find((candidate) => candidate.fileId === authoritative.fileId);
        if (file) {
          mergeFile(file.key, authoritative);
        }
      });
    }

    let currentPrimary = submittedFiles.find((file) => file.wasPrimaryPhoto);
    let desiredPrimary = submittedFiles.find((file) => file.key === desiredPrimaryKey);
    if (currentPrimary && !currentPrimary.deleted && currentPrimary.key !== desiredPrimaryKey) {
      const target = desiredPrimary ?? currentPrimary;
      const updated = await run(() =>
        getAction('HrUpdatePersonFile')({
          parameters: { personId: targetPersonId, personFileId: String(target.personFileId) },
          payload: {
            version: target.version!,
            displayName: target.displayName.trim() || target.fileName,
            categoryId: Number(target.categoryId),
            isPrimaryPhoto: Boolean(desiredPrimary),
          },
        })
      );
      if (desiredPrimary) {
        currentPrimary.version = (currentPrimary.version ?? 0) + 1;
        currentPrimary.isPrimaryPhoto = false;
      }
      mergeFile(target.key, updated);
      currentPrimary = desiredPrimary;
    } else if (!currentPrimary && desiredPrimary) {
      const target = desiredPrimary;
      const updated = await run(() =>
        getAction('HrUpdatePersonFile')({
          parameters: { personId: targetPersonId, personFileId: String(target.personFileId) },
          payload: {
            version: target.version!,
            displayName: target.displayName.trim() || target.fileName,
            categoryId: Number(target.categoryId),
            isPrimaryPhoto: true,
          },
        })
      );
      mergeFile(target.key, updated);
      currentPrimary = target;
    }

    const orderedDeletions = currentPrimary?.deleted
      ? [currentPrimary, ...deletedFiles.filter((file) => file.key !== currentPrimary?.key)]
      : deletedFiles;
    /** Persists one deletion, optionally promoting a versioned replacement atomically. */
    const persistDeletion = (file: HrPersonFormFile, replacement?: HrPersonFormFile) =>
      getAction('HrDeletePersonFile')({
        parameters: { personId: targetPersonId, personFileId: String(file.personFileId) },
        payload: {
          version: file.version!,
          ...(replacement
            ? {
                replacementPersonFileId: replacement.personFileId!,
                replacementVersion: replacement.version!,
              }
            : {}),
        },
      });
    for (const file of orderedDeletions) {
      desiredPrimary = submittedFiles.find((candidate) => candidate.key === desiredPrimaryKey);
      const promotesReplacement = file.wasPrimaryPhoto && desiredPrimary;
      await run(persistDeletion.bind(null, file, promotesReplacement ? desiredPrimary : undefined));
      if (promotesReplacement) {
        desiredPrimary!.version = (desiredPrimary!.version ?? 0) + 1;
        desiredPrimary!.isPrimaryPhoto = true;
      }
    }

    return total;
  };

  const sectionLayout = useEvidenceSections();
  const layoutError =
    sectionLayout.error ||
    (!sectionLayout.loading &&
      personEvidence.some(
        (field) =>
          !sectionLayout.sections.some(
            (section) => section.module === 'people' && section.sectionId === field.sectionId
          )
      ));
  const submitPerson = form.onSubmit(async (values) => {
    if (sectionLayout.loading || layoutError || optionsLoading) return;
    let hasEvidenceError = false;
    personEvidence.forEach((definition) => {
      if (
        definition.isRequired &&
        definition.fieldType !== 'checkbox' &&
        `${values.evidence[String(definition.evidenceFieldId)] ?? ''}`.trim() === ''
      ) {
        form.setFieldError(`evidence.${definition.evidenceFieldId}`, 'Pole je povinné');
        hasEvidenceError = true;
      }
    });
    values.terms.forEach((term, index) => {
      if (term.deleted || (term.lifecycleStatus && term.lifecycleStatus !== 'active')) return;
      if (!term.termTypeId) {
        form.setFieldError(`terms.${index}.termTypeId`, 'Vyberte typ termínu');
        hasEvidenceError = true;
      }
      if (!term.nextDueDate) {
        form.setFieldError(`terms.${index}.nextDueDate`, 'Vyberte najbližší termín');
        hasEvidenceError = true;
      }
    });
    if (formCanAccessFiles) {
      values.files.forEach((file, index) => {
        if (file.deleted) return;
        if (!file.personFileId && !file.fileId) {
          form.setFieldError(`files.${index}.fileId`, 'Nahrajte súbor');
          hasEvidenceError = true;
        }
        if (!file.categoryId) {
          form.setFieldError(`files.${index}.categoryId`, 'Vyberte kategóriu');
          hasEvidenceError = true;
        }
      });
    }
    if (hasEvidenceError) {
      return;
    }
    const payload = {
      ...(creating ? {} : { version: person.version }),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      departmentId: Number(values.departmentId),
      userId: values.userId ? Number(values.userId) : undefined,
      evidenceFields: personEvidence.map((definition) => {
        const value = values.evidence[String(definition.evidenceFieldId)];
        const hasValue = value !== undefined && value !== null && value !== '';
        return {
          evidenceFieldId: definition.evidenceFieldId,
          fieldType: definition.fieldType,
          fieldValue: definition.fieldType === 'text' && hasValue ? String(value) : undefined,
          fieldNumberValue: definition.fieldType === 'number' && hasValue ? Number(value) : undefined,
          fieldDateValue: definition.fieldType === 'date' && hasValue ? String(value) : undefined,
          fieldSelectValue: definition.fieldType === 'select' && hasValue ? String(value) : undefined,
          fieldPredefinedValue: definition.fieldType === 'predefined' && hasValue ? String(value) : undefined,
          fieldBooleanValue: definition.fieldType === 'checkbox' ? value === true : undefined,
        };
      }),
    };

    setSaving(true);
    setConflict(false);
    try {
      if (creating) {
        const created = await getAction('HrCreatePerson')({ payload: payload as any });
        try {
          await persistPersonRelated(String(created.personId), values, formCanAccessFiles);
        } catch (relatedError: any) {
          notifications.show({
            title: copy
              ? 'Kópia osoby bola vytvorená, súvisiace položky nie sú úplné'
              : 'Osoba bola vytvorená, súvisiace položky nie sú úplné',
            message: `Uložené operácie: ${relatedError.completed}/${relatedError.total}. Otvorená bude autoritatívna stránka osoby.`,
            color: 'orange',
          });
          form.resetDirty(values);
          navigate(HR_EDIT_PERSON_PAGE_PATH.insert({ personId: String(created.personId) }));
          return;
        }
        notifications.show({
          title: copy ? 'Osoba bola skopírovaná' : 'Osoba bola vytvorená',
          message: `${created.firstName} ${created.lastName} bola úspešne uložená.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });
        form.resetDirty(values);
        navigate(HR_EDIT_PERSON_PAGE_PATH.insert({ personId: String(created.personId) }));
      } else {
        await getAction('HrUpdatePerson')({ parameters: { personId: personId! }, payload: payload as any });
        try {
          await persistPersonRelated(personId!, values, formCanAccessFiles);
        } catch (relatedError: any) {
          notifications.show({
            title: 'Osoba bola uložená, súvisiace položky nie sú úplné',
            message: `Uložené operácie: ${relatedError.completed}/${relatedError.total}. Dáta boli znovu načítané.`,
            color: 'orange',
          });
          setRelatedFormPersonId(null);
          await loadPerson();
          await loadRelated();
          return;
        }
        notifications.show({
          title: 'Osoba bola aktualizovaná',
          message: 'Zmeny boli uložené a znovu načítané zo servera.',
          color: SUCCESS_NOTIFICATION_COLOR,
        });
        await loadPerson();
        setRelatedFormPersonId(null);
        await loadRelated();
      }
    } catch (caught) {
      if (errorStatus(caught) === 409) {
        setConflict(true);
      } else {
        panic(caught as Error);
      }
    } finally {
      setSaving(false);
    }
  });

  /** Initializes the selected term workflow modal from authoritative row data. */
  const openTermModal = useCallback(
    async (mode: 'create' | 'execute' | 'pair' | 'reactivate', term?: any) => {
      setSelectedTerm(term ?? null);
      setCertificateOptions([]);
      termForm.reset();
      setTermModal(mode);
      if (term) {
        const common = {
          certificateNumber: term.certificateNumber ?? '',
          certificateFileId: term.certificateFileId ?? '',
          stampFileId: term.stampFileId ?? '',
        };
        if (mode === 'pair') {
          termForm.setValues({
            ...termForm.values,
            ...common,
            renewalDate: term.renewalDate ?? term.nextDueDate ?? '',
          });
          setCertificateOptionsLoading(true);
          try {
            setCertificateOptions(
              await getAction('HrListCertificateOptions')({ parameters: { termId: String(term.termId) } })
            );
          } catch (caught) {
            panic(caught as Error);
          } finally {
            setCertificateOptionsLoading(false);
          }
        } else if (mode === 'reactivate') {
          termForm.setValues({ ...termForm.values, nextDueDate: term.nextDueDate ?? '' });
        }
      }
    },
    [getAction, termForm]
  );

  const submitTerm = termForm.onSubmit(async (values) => {
    if (termModal === 'create' && (!values.termTypeId || !values.nextDueDate)) {
      if (!values.termTypeId) termForm.setFieldError('termTypeId', 'Vyberte typ termínu');
      if (!values.nextDueDate) termForm.setFieldError('nextDueDate', 'Vyberte najbližší termín');
      return;
    }
    try {
      if (termModal === 'create') {
        const payload = {
          termTypeId: Number(values.termTypeId),
          nextDueDate: values.nextDueDate,
          recurrence:
            values.recurrenceUnit !== 'none'
              ? { quantity: values.recurrenceQuantity, unit: values.recurrenceUnit }
              : null,
          note: values.note || undefined,
          checkedVariantIds: values.checkedVariantIds,
        };
        await getAction('HrCreateTerm')({
          parameters: { personId: personId! },
          payload: payload as any,
        });
      } else if (termModal === 'pair') {
        await getAction('HrPairCertificate')({
          parameters: { termId: String(selectedTerm.termId) },
          payload: {
            version: selectedTerm.version,
            certificateId: values.certificateId ? Number(values.certificateId) : undefined,
            certificateNumber: values.certificateNumber || undefined,
            renewalDate: values.renewalDate || undefined,
            certificateFileId: values.certificateFileId || undefined,
            stampFileId: values.stampFileId || undefined,
          },
        });
      } else if (termModal === 'reactivate') {
        await getAction('HrSetTermStatus')({
          parameters: { termId: String(selectedTerm.termId) },
          payload: { version: selectedTerm.version, status: 'active', nextDueDate: values.nextDueDate },
        });
      }
      notifications.show({
        title: 'HR termín bol aktualizovaný',
        message: 'Výsledok bol uložený a načítaný zo servera.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });
      if (termMode) {
        await loadRelated();
        termForm.resetDirty(values);
        navigate(HR_EDIT_PERSON_PAGE_PATH.insert({ personId: personId! }));
      } else {
        setTermModal(null);
        await loadRelated();
      }
    } catch (caught) {
      if (errorStatus(caught) === 409) {
        setConflict(true);
      } else {
        panic(caught as Error);
      }
    }
  });

  /** Explicitly unpairs a certificate while preserving both certificate and term history. */
  const unpairCertificate = (term: any) => {
    confirm({
      title: 'Odpojiť osvedčenie',
      content: 'Termín sa vyradí, osvedčenie a jeho história zostanú zachované.',
      primaryButtonText: 'Odpojiť',
      secondaryButtonText: 'Zrušiť',
      onConfirm: async () => {
        try {
          await getAction('HrUnpairCertificate')({ parameters: { termId: String(term.termId) } });
          await loadRelated();
        } catch (caught) {
          panic(caught as Error);
        }
      },
    });
  };

  useEffect(() => {
    const termId = searchParams.get('termId');
    const action = searchParams.get('action');
    const actionKey = termId && action ? `${termId}:${action}` : '';
    if (!actionKey || handledDeepAction === actionKey || terms.length === 0) {
      return;
    }
    const term = terms.find((candidate) => String(candidate.termId) === termId);
    if (!term) {
      return;
    }
    setHandledDeepAction(actionKey);
    if (action === 'history') {
      navigate(HR_TERM_PAGE_PATH.insert({ termId: String(term.termId) }), { replace: true });
    } else if (
      person?.actionPermissions?.edit &&
      person.status !== 'inactive' &&
      ((action === 'execute' && term.actionPermissions?.execute && (term.rawStatus ?? term.status) === 'active') ||
        (action === 'reactivate' && term.actionPermissions?.edit && (term.rawStatus ?? term.status) === 'archived'))
    ) {
      openTermModal(action, term);
    }
  }, [handledDeepAction, navigate, openTermModal, person, searchParams, terms]);

  /** Persists HR metadata for a binary already uploaded through File Manager. */
  const addUploadedFile = async () => {
    const { fileId, fileName, mimeType, categoryId } = fileCreateForm.values;
    if (!fileId || !categoryId || !personId) {
      return;
    }
    try {
      await getAction('HrAddPersonFiles')({
        parameters: { personId },
        payload: {
          files: [
            {
              fileId,
              fileName: fileName || 'súbor',
              categoryId: Number(categoryId),
              isPrimaryPhoto: mimeType.startsWith('image/') && !files.some((file) => Boolean(file.isPrimaryPhoto)),
            },
          ],
        },
      });
      fileCreateForm.resetDirty(fileCreateForm.values);
      navigate(HR_EDIT_PERSON_PAGE_PATH.insert({ personId: personId! }));
    } catch (caught) {
      setError('Binárny súbor bol nahraný, ale jeho HR metadata sa nepodarilo uložiť. Zobrazenie bolo obnovené.');
      await loadRelated();
    }
  };

  /** Updates mutable HR metadata for an existing person-file association. */
  const updateFile = async (file: any, categoryId: string | null, displayName?: string) => {
    if (!categoryId) {
      return false;
    }
    try {
      await getAction('HrUpdatePersonFile')({
        parameters: { personId: personId!, personFileId: String(file.personFileId) },
        payload: {
          version: file.version,
          displayName: displayName?.trim() || file.displayName || file.originalName || 'súbor',
          categoryId: Number(categoryId),
        },
      });
      await loadRelated();
      return true;
    } catch (caught) {
      if (errorStatus(caught) === 409) {
        setConflict(true);
      } else {
        panic(caught as Error);
      }
      return false;
    }
  };

  /** Opens the explicit versioned metadata form for one person-file row. */
  const openFileEdit = useCallback(
    (file: any) => {
      setEditingFile(file);
      const authoritativeValues = {
        displayName: file.displayName ?? file.originalName ?? '',
        categoryId: String(file.categoryId),
      };
      fileForm.setValues(authoritativeValues);
      fileForm.resetDirty(authoritativeValues);
    },
    [fileForm]
  );

  useEffect(() => {
    if (!termMode || !person || !relatedLoaded || initializedStandaloneRoute === standaloneRouteKey) return;
    if (!person.actionPermissions.manageTerms || person.status !== 'active') {
      setError('Nemáte oprávnenie upravovať termíny tejto osoby.');
      setInitializedStandaloneRoute(standaloneRouteKey);
      return;
    }
    openTermModal('create');
    setInitializedStandaloneRoute(standaloneRouteKey);
  }, [initializedStandaloneRoute, openTermModal, person, relatedLoaded, standaloneRouteKey, termMode]);

  useEffect(() => {
    if (!fileMode || !person || !relatedLoaded || initializedStandaloneRoute === standaloneRouteKey) return;
    if (!person.actionPermissions.edit || !person.actionPermissions.accessFiles || person.status !== 'active') {
      setError('Nemáte oprávnenie upravovať súbory tejto osoby.');
      setInitializedStandaloneRoute(standaloneRouteKey);
      return;
    }
    if (fileMode === 'create') {
      fileCreateForm.reset();
      setInitializedStandaloneRoute(standaloneRouteKey);
      return;
    }
    const file = files.find((candidate) => String(candidate.personFileId) === personFileId);
    if (!file) {
      setError('Súbor neexistuje alebo ho nie je možné upraviť.');
      setInitializedStandaloneRoute(standaloneRouteKey);
      return;
    }
    openFileEdit(file);
    setInitializedStandaloneRoute(standaloneRouteKey);
  }, [
    fileMode,
    fileCreateForm,
    files,
    initializedStandaloneRoute,
    openFileEdit,
    person,
    personFileId,
    relatedLoaded,
    standaloneRouteKey,
  ]);

  const submitFileEdit = fileForm.onSubmit(async (values) => {
    const saved = await updateFile(editingFile, values.categoryId, values.displayName);
    if (saved) {
      fileForm.resetDirty(values);
      navigate(HR_EDIT_PERSON_PAGE_PATH.insert({ personId: personId! }));
    }
  });

  const inactive = Boolean(edit && person?.status === 'inactive');
  const canEdit = (creating && !error) || (edit && person?.actionPermissions?.edit && !inactive);
  const canManageInlineFiles = canEdit && formCanAccessFiles;
  const canViewInlineFiles = edit && formCanAccessFiles;
  const termsLoading = Boolean((edit || copy) && person && (!relatedLoaded || relatedFormPersonId !== person.personId));
  const highlightedTermAvailable =
    !termsLoading &&
    highlightedTermId !== undefined &&
    form.values.terms.some((term) => !term.deleted && term.termId === highlightedTermId);

  useEffect(() => {
    if (!highlightedTermAvailable) {
      setIsTermHighlightVisible(false);
      return;
    }

    setIsTermHighlightVisible(true);
    const timeoutId = window.setTimeout(() => {
      setIsTermHighlightVisible(false);
    }, 10000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [highlightedTermAvailable, highlightedTermId]);

  useEffect(() => {
    if (!termsLoading && isTermHighlightVisible && highlightedTermRef.current) {
      highlightedTermRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [highlightedTermId, isTermHighlightVisible, termsLoading]);

  /** Removes a staged term immediately or confirms exclusion of a persisted term. */
  const removeInlineTerm = (index: number, term: PersonFormValues['terms'][number]) => {
    if (!term.termId) {
      form.removeListItem('terms', index);
      return;
    }

    const termTypeName = termTypes.find((type) => String(type.termTypeId) === term.termTypeId)?.name ?? 'termín';
    confirm({
      title: 'Vyradiť termín',
      content: `Naozaj si prajete vyradiť termín „${termTypeName}“? História zostane zachovaná.`,
      primaryButtonText: 'Vyradiť',
      secondaryButtonText: 'Zrušiť',
      onConfirm: () => form.setFieldValue(`terms.${index}.deleted`, true),
    });
  };

  const overviewForm = (
    <FormWrapper
      primaryButtonText={copy ? 'Skopírovať' : create ? 'Vytvoriť' : 'Uložiť'}
      primaryIcon={copy ? <IconCopy stroke={1.5} /> : create ? <IconPlus stroke={1.5} /> : <IconCheck stroke={1.5} />}
      hidePrimaryButton={!canEdit}
      hideSecondaryButton={!canEdit}
      secondaryButtonText="Zrušiť"
      onSecondaryClick={() => navigate(HR_PEOPLE_PAGE_PATH.original)}
      skipSecondaryButtonConfirm={!form.isDirty()}
      onSubmit={submitPerson}
      loading={saving || optionsLoading || sectionLayout.loading}
      contentLoading={optionsLoading || sectionLayout.loading}
    >
      {layoutError && (
        <Alert color="red">Sekcie alebo evidenčné polia sa nepodarilo načítať. Obnovte stránku pred uložením.</Alert>
      )}
      <EvidenceSections
        module="people"
        sections={sectionLayout.sections}
        fields={personEvidence}
        builtinGroups={{
          basic_info: (
            <FormInputGroup groupTitle="Základné info">
              <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                <TextInput
                  size="lg"
                  label="Meno"
                  withAsterisk
                  disabled={!canEdit}
                  {...form.getInputProps('firstName')}
                />
                <TextInput
                  size="lg"
                  label="Priezvisko"
                  withAsterisk
                  disabled={!canEdit}
                  {...form.getInputProps('lastName')}
                />
              </SimpleGrid>
              <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                {canEdit ? (
                  <DepartmentSelect
                    size="lg"
                    label={departmentText('Položka úrovne', 'Stredisko')}
                    groupByOrganization
                    permissionSlug="manage-hr"
                    showDiscarded={false}
                    organizationId={edit || copy ? person?.organizationId : undefined}
                    value={form.values.departmentId}
                    error={form.errors.departmentId}
                    onChange={changeDepartment}
                  />
                ) : (
                  <TextInput
                    size="lg"
                    label={departmentText('Položka úrovne', 'Stredisko')}
                    value={person?.departmentName ?? ''}
                    disabled
                  />
                )}
                {canEdit ? (
                  <Select
                    size="lg"
                    label="Prepojený používateľ (nepovinné)"
                    clearable
                    data={linkedUsers.map((user) => ({
                      value: String(user.userId),
                      label: `${user.fullName} (${user.email})`,
                    }))}
                    disabled={!formOrganizationId || optionsLoading}
                    {...form.getInputProps('userId')}
                  />
                ) : (
                  <TextInput
                    size="lg"
                    label="Prepojený používateľ"
                    value={person?.userEmail ?? 'Bez prepojenia'}
                    disabled
                  />
                )}
              </SimpleGrid>
            </FormInputGroup>
          ),
        }}
        renderFields={(fields) =>
          Array.from({ length: Math.ceil(fields.length / 2) }, (_, rowIndex) => (
            <SimpleGrid key={rowIndex} cols={2} spacing={isTablet ? 20 : 40}>
              {fields.slice(rowIndex * 2, rowIndex * 2 + 2).map((field: any) => {
                const path = `evidence.${field.evidenceFieldId}`;
                return (
                  <EvidenceValueInput
                    key={field.evidenceFieldId}
                    definition={field}
                    value={
                      form.values.evidence[String(field.evidenceFieldId)] ??
                      (field.fieldType === 'checkbox' ? false : '')
                    }
                    disabled={!canEdit}
                    error={form.errors[path]}
                    onChange={(value) => form.setFieldValue(path, value ?? '')}
                  />
                );
              })}
            </SimpleGrid>
          ))
        }
      />
      {(creating || edit) && (
        <>
          <Divider mt={40} color="gray.1" />
          <Flex align="center" ml={isTablet ? 20 : 40} mt={40} mb={16} gap={24}>
            <Group spacing={8} align="center">
              <IconCalendar stroke="1.5" height={24} width={24} color={theme.colors.gray[7]} />
              <H3Medium color="gray.7">Sledovanie termínov</H3Medium>
            </Group>
          </Flex>

          {termsLoading && (
            <Group position="center" py={40}>
              <Loader aria-label="Načítavam termíny osoby" />
            </Group>
          )}
          {!termsLoading &&
            form.values.terms.map((term, index) => {
              if (term.deleted) {
                return null;
              }
              const termCanEdit = canEdit && (!term.lifecycleStatus || term.lifecycleStatus === 'active');
              const isHighlightedTerm =
                isTermHighlightVisible && highlightedTermId !== undefined && term.termId === highlightedTermId;
              const termVariants =
                termTypes.find((type) => String(type.termTypeId) === term.termTypeId)?.variants ?? [];
              return (
                <Box
                  key={term.key}
                  ref={isHighlightedTerm ? highlightedTermRef : undefined}
                  data-hr-person-term={term.termId ?? term.key}
                  data-hr-person-term-highlighted={isHighlightedTerm ? 'true' : undefined}
                  p={isTablet ? 20 : 40}
                  ml={isTablet ? 20 : 40}
                  mr={24}
                  mb={24}
                  bg={isHighlightedTerm ? 'yellow.0' : 'gray.0'}
                  sx={{ borderRadius: 4 }}
                >
                  <Stack spacing={24}>
                    {term.lifecycleStatus && term.lifecycleStatus !== 'active' && (
                      <Group spacing={12}>
                        <Badge color="gray">{formatHrStatus(term.lifecycleStatus)}</Badge>
                      </Group>
                    )}
                    <SimpleGrid data-hr-term-layout="primary-fields" cols={2} spacing={isTablet ? 20 : 40}>
                      <Box data-hr-term-field="term-type">
                        <Select
                          size="lg"
                          label="Typ termínu"
                          withAsterisk
                          disabled={!termCanEdit || Boolean(term.termId)}
                          data={[
                            ...(term.termTypeId &&
                            !termTypes.some((type) => String(type.termTypeId) === term.termTypeId)
                              ? [{ value: term.termTypeId, label: term.termTypeName ?? `Typ ${term.termTypeId}` }]
                              : []),
                            ...termTypes.map((type) => ({
                              value: String(type.termTypeId),
                              label: type.name ?? `Typ ${type.termTypeId}`,
                            })),
                          ]}
                          value={term.termTypeId}
                          error={form.errors[`terms.${index}.termTypeId`]}
                          onChange={(value) => {
                            const selected = termTypes.find((type) => String(type.termTypeId) === value);
                            const certificateType = selected?.kind === 'technician-certificate';
                            form.setFieldValue(`terms.${index}.termTypeId`, value ?? '');
                            form.setFieldValue(`terms.${index}.termTypeName`, selected?.name ?? '');
                            form.setFieldValue(
                              `terms.${index}.recurrenceQuantity`,
                              selected?.defaultRecurrence?.quantity ?? (certificateType ? 1 : 0)
                            );
                            form.setFieldValue(
                              `terms.${index}.recurrenceUnit`,
                              selected?.defaultRecurrence?.unit ?? (certificateType ? 'years' : 'none')
                            );
                            form.setFieldValue(`terms.${index}.checkedVariantIds`, []);
                            form.setFieldValue(`terms.${index}.changed`, true);
                          }}
                        />
                      </Box>
                      <Box data-hr-term-field="variants">
                        {termVariants.length > 0 && (
                          <Input.Wrapper size="lg" label="Varianty">
                            <HrTermVariantCheckboxes
                              variants={termVariants}
                              checkedVariantIds={term.checkedVariantIds}
                              disabled={!termCanEdit}
                              onChange={(variantIds) => {
                                form.setFieldValue(`terms.${index}.checkedVariantIds`, variantIds);
                                form.setFieldValue(`terms.${index}.changed`, true);
                              }}
                            />
                          </Input.Wrapper>
                        )}
                      </Box>
                      <Box data-hr-term-field="last-execution-date">
                        <DateInput
                          size="lg"
                          label="Dátum posledného vykonania"
                          disabled={!termCanEdit}
                          value={term.lastExecutionDate}
                          onChange={(value) => {
                            form.setFieldValue(`terms.${index}.lastExecutionDate`, value ?? '');
                            form.setFieldValue(`terms.${index}.changed`, true);
                          }}
                        />
                      </Box>
                      <Box data-hr-term-field="next-due-date">
                        <DateInput
                          size="lg"
                          label="Najbližší termín"
                          withAsterisk
                          disabled={!termCanEdit}
                          value={term.nextDueDate}
                          error={form.errors[`terms.${index}.nextDueDate`]}
                          onChange={(value) => {
                            form.setFieldValue(`terms.${index}.nextDueDate`, value ?? '');
                            form.setFieldValue(`terms.${index}.changed`, true);
                          }}
                        />
                      </Box>
                    </SimpleGrid>
                    <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                      <NumberInput
                        size="lg"
                        min={1}
                        label="Opakovanie"
                        disabled={!termCanEdit || term.recurrenceUnit === 'none'}
                        value={term.recurrenceQuantity}
                        onChange={(value) => {
                          form.setFieldValue(`terms.${index}.recurrenceQuantity`, Number(value));
                          form.setFieldValue(`terms.${index}.changed`, true);
                        }}
                      />
                      <Select
                        size="lg"
                        label="Jednotka opakovania"
                        disabled={!termCanEdit}
                        data={[
                          ...(termTypes.find((type) => String(type.termTypeId) === term.termTypeId)?.kind ===
                          'technician-certificate'
                            ? []
                            : [{ value: 'none', label: 'Bez opakovania' }]),
                          { value: 'days', label: 'Dni' },
                          { value: 'weeks', label: 'Týždne' },
                          { value: 'months', label: 'Mesiace' },
                          { value: 'years', label: 'Roky' },
                        ]}
                        value={term.recurrenceUnit}
                        onChange={(value) => {
                          const recurrenceUnit = value ?? 'none';
                          form.setFieldValue(`terms.${index}.recurrenceUnit`, recurrenceUnit);
                          if (recurrenceUnit === 'none') {
                            form.setFieldValue(`terms.${index}.recurrenceQuantity`, 0);
                          } else if (term.recurrenceQuantity < 1) {
                            form.setFieldValue(`terms.${index}.recurrenceQuantity`, 1);
                          }
                          form.setFieldValue(`terms.${index}.changed`, true);
                        }}
                      />
                    </SimpleGrid>
                    <Box data-hr-term-field="note" w="100%">
                      <Textarea
                        size="lg"
                        label="Poznámka"
                        disabled={!termCanEdit}
                        value={term.note}
                        onChange={(event) => {
                          form.setFieldValue(`terms.${index}.note`, event.currentTarget.value);
                          form.setFieldValue(`terms.${index}.changed`, true);
                        }}
                      />
                    </Box>
                    <Group position="right">
                      {term.termId ? (
                        <HrPersonTermActions
                          data={terms.find((candidate) => candidate.termId === term.termId) ?? term}
                          inactive={inactive}
                          readonly={!person?.actionPermissions?.edit}
                          openTermModal={openTermModal}
                          unpairCertificate={unpairCertificate}
                          setTermStatus={(selected) =>
                            term.lifecycleStatus === 'active'
                              ? removeInlineTerm(index, term)
                              : openTermModal('reactivate', selected)
                          }
                        />
                      ) : termCanEdit ? (
                        <Button
                          type="button"
                          variant="danger-secondary"
                          leftIcon={<IconTrashX stroke={1.5} size={24} />}
                          fullWidth={isMobile}
                          onClick={() => removeInlineTerm(index, term)}
                        >
                          Odobrať
                        </Button>
                      ) : null}
                    </Group>
                  </Stack>
                </Box>
              );
            })}
          {canEdit && !termsLoading && (
            <Group position="left" mt="md" ml={isTablet ? 20 : 40}>
              <Button
                type="button"
                variant="subtle"
                size="md"
                fullWidth={isMobile}
                leftIcon={<IconPlus stroke="1.5" height={24} width={24} />}
                onClick={() =>
                  form.insertListItem('terms', {
                    key: randomId(),
                    termTypeId: '',
                    lifecycleStatus: 'active',
                    lastExecutionDate: '',
                    nextDueDate: '',
                    recurrenceQuantity: 0,
                    recurrenceUnit: 'none',
                    note: '',
                    checkedVariantIds: [],
                    changed: true,
                    deleted: false,
                  })
                }
              >
                Pridať ďalší termín
              </Button>
            </Group>
          )}

          {(canManageInlineFiles || canViewInlineFiles) && (
            <>
              <Divider mt={40} color="gray.1" />
              <Flex align="center" ml={isTablet ? 20 : 40} mt={40} mb={16} gap={24}>
                <Group spacing={8} align="center">
                  <IconFile stroke="1.5" height={24} width={24} color={theme.colors.gray[7]} />
                  <H3Medium color="gray.7">Súbory</H3Medium>
                </Group>
              </Flex>
              <Box pl={isTablet ? 20 : 40} pr={isTablet ? 20 : 40} pb={40}>
                <HrPersonFiles
                  files={form.values.files}
                  categories={fileCategories}
                  readonly={!canManageInlineFiles}
                  onAdd={(uploadedFiles, category) => {
                    let hasPrimaryPhoto = form.values.files.some((file) => !file.deleted && file.isPrimaryPhoto);
                    uploadedFiles.forEach((uploaded) => {
                      const isImage = uploaded.fileType.startsWith('image/');
                      const isPrimaryPhoto = isImage && !hasPrimaryPhoto;
                      hasPrimaryPhoto = hasPrimaryPhoto || isPrimaryPhoto;
                      form.insertListItem('files', {
                        key: randomId(),
                        fileId: uploaded.fileId!,
                        fileName: uploaded.fileName,
                        displayName: uploaded.fileName,
                        categoryId: category.categoryId,
                        categoryName: category.categoryName,
                        uploadedAt: new Date().toISOString(),
                        mimeType: uploaded.fileType,
                        isPrimaryPhoto,
                        wasPrimaryPhoto: false,
                        changed: true,
                        deleted: false,
                      });
                    });
                  }}
                  onChange={(index, changes) => {
                    Object.entries(changes).forEach(([field, value]) =>
                      form.setFieldValue(`files.${index}.${field}`, value)
                    );
                    form.setFieldValue(`files.${index}.changed`, true);
                  }}
                  onSetPrimary={(index) => {
                    const selectTarget = !form.values.files[index].isPrimaryPhoto;
                    form.setFieldValue(
                      'files',
                      form.values.files.map((file, fileIndex) => ({
                        ...file,
                        isPrimaryPhoto: selectTarget && fileIndex === index,
                      }))
                    );
                  }}
                  onRemove={(index) => {
                    const removed = form.values.files[index];
                    let nextFiles = removed.personFileId
                      ? form.values.files.map((file, fileIndex) =>
                          fileIndex === index ? { ...file, deleted: true, isPrimaryPhoto: false } : file
                        )
                      : form.values.files.filter((_, fileIndex) => fileIndex !== index);
                    if (removed.isPrimaryPhoto) {
                      const replacementIndex = nextFiles.findIndex(
                        (file) => !file.deleted && file.mimeType?.startsWith('image/')
                      );
                      if (replacementIndex >= 0) {
                        nextFiles = nextFiles.map((file, fileIndex) => ({
                          ...file,
                          isPrimaryPhoto: fileIndex === replacementIndex,
                        }));
                      }
                    }
                    form.setFieldValue('files', nextFiles);
                  }}
                />
              </Box>
            </>
          )}
        </>
      )}
    </FormWrapper>
  );

  const standaloneTermForm = (
    <HrPersonTermForm
      form={termForm}
      termTypes={termTypes}
      isTablet={isTablet}
      onCancel={() => navigate(HR_EDIT_PERSON_PAGE_PATH.insert({ personId: personId! }))}
      onSubmit={submitTerm}
    />
  );

  const standaloneFileForm = (
    <HrPersonFileForm
      mode={fileMode!}
      createForm={fileCreateForm}
      editForm={fileForm}
      categories={fileCategories}
      isTablet={isTablet}
      onCancel={() => navigate(HR_EDIT_PERSON_PAGE_PATH.insert({ personId: personId! }))}
      onCreate={addUploadedFile}
      onEditSubmit={submitFileEdit}
    />
  );

  if (loading || (standaloneRouteKey && initializedStandaloneRoute !== standaloneRouteKey)) {
    return (
      <DashboardLayout breadcrumbs={[{ title: 'Načítavanie osoby' }]}>
        <Group position="center" py={80}>
          <Loader />
        </Group>
      </DashboardLayout>
    );
  }

  if (error && !create && !person) {
    return (
      <DashboardLayout breadcrumbs={[{ title: 'Osoba' }]}>
        <Alert color="red" title="Osobu nie je možné zobraziť">
          {error}
          <Button mt={12} variant="tertiary" onClick={loadPerson}>
            Načítať znova
          </Button>
        </Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={
        copy
          ? `Kopírovať ${person.firstName} ${person.lastName}`
          : create
          ? 'Pridať osobu'
          : edit
          ? person.actionPermissions.edit
            ? `Upraviť ${person.firstName} ${person.lastName}`
            : `${person.firstName} ${person.lastName}`
          : termMode
          ? `Pridať termín – ${person.firstName} ${person.lastName}`
          : fileMode
          ? `${fileMode === 'create' ? 'Pridať' : 'Upraviť'} súbor – ${person.firstName} ${person.lastName}`
          : `${person.firstName} ${person.lastName}`
      }
      breadcrumbs={[
        { title: 'Osoby', link: HR_PEOPLE_PAGE_PATH.original },
        {
          title: copy
            ? 'Kopírovať osobu'
            : create
            ? 'Pridať osobu'
            : edit && person.actionPermissions.edit
            ? 'Upraviť osobu'
            : `${person.firstName} ${person.lastName}`,
        },
      ]}
    >
      <Stack p={creating || edit || termMode || fileMode ? 0 : 24}>
        {inactive && (
          <Alert color="orange" title="Vyradená osoba">
            Údaje, súbory a história zostávajú zachované. Úpravy, termínové akcie a upozornenia sú pozastavené.
          </Alert>
        )}
        {conflict && (
          <Alert color="orange" title="Údaje sa medzičasom zmenili">
            Znovu načítajte autoritatívny stav a zopakujte zmenu.
            <Button ml={12} variant="tertiary" onClick={loadPerson}>
              Znovu načítať
            </Button>
          </Alert>
        )}
        {error && <Alert color="red">{error}</Alert>}

        {termMode ? standaloneTermForm : fileMode ? standaloneFileForm : overviewForm}
      </Stack>

      {termModal === 'execute' && selectedTerm && (
        <HrExecuteTermModal
          key={`${selectedTerm.termId}-${selectedTerm.version}`}
          term={selectedTerm}
          fileCategories={fileCategories}
          opened
          onClose={() => setTermModal(null)}
          onConflict={() => setConflict(true)}
          onExecuted={loadRelated}
        />
      )}

      <HrTermStateModal
        mode={termModal === 'pair' || termModal === 'reactivate' ? termModal : null}
        form={termForm}
        certificateOptions={certificateOptions}
        certificateOptionsLoading={certificateOptionsLoading}
        onClose={() => setTermModal(null)}
        onSubmit={submitTerm}
      />
    </DashboardLayout>
  );
}
